/**
 * A sales demo's tutorial (useTutorial.ts): the extension's steps for this tenant and language,
 * and whether this sign-in has had it.
 *
 * "Each sign-in" is the session, not the page: a sign-in makes a new session, whose user and
 * expiry (me.php `user_id`, `expires_at`, fixed for the session's life) name it. The tutorial
 * starts once per such key and browser; a reload, a second tab or the next morning on the same
 * session don't start it again, the next sign-in does.
 */
import type { ExtensionView, LocalizedText, TutorialStep } from '@/extensions/types'

/** A step ready to show: where it is, in the reader's language. */
export interface ResolvedStep {
    id: string
    /** The view's address, opened before the step; null: the step stays on the page. */
    path: string | null
    element: string | null
    click: string | null
    close: string | null
    title: string
    text: string
    side: TutorialStep['side'] | null
    interactive: boolean
}

function localized(text: LocalizedText, locale: string): string {
    return text[locale as keyof LocalizedText] ?? text.en ?? ''
}

/**
 * The steps this tenant shows: a step on a view the tenant doesn't show (its menu, the role's
 * pages) is left out, and so is one without text in any language.
 */
export function resolveSteps(steps: readonly TutorialStep[] | undefined, views: readonly Pick<ExtensionView, 'id' | 'path'>[], locale: string): ResolvedStep[] {
    const resolved: ResolvedStep[] = []
    for (const step of steps ?? []) {
        const view = step.view === undefined ? null : views.find((v) => v.id === step.view)
        if (view === undefined) continue
        const title = localized(step.title, locale)
        const text = localized(step.text, locale)
        if (!title && !text) continue
        resolved.push({
            id: step.id,
            path: view?.path ?? null,
            element: step.element ?? null,
            click: step.click ?? null,
            close: step.close ?? null,
            title,
            text,
            side: step.side ?? null,
            interactive: step.interactive === true,
        })
    }
    return resolved
}

/** The sign-in a session belongs to (see above). */
export function signInKey(session: { user_id: number; expires_at?: number | null }): string {
    return `${session.user_id}:${session.expires_at ?? ''}`
}

const storageKey = (slug: string) => `distribuai.tutorial.${slug}`
// Without storage (blocked site data), at least this page doesn't start it twice.
const seenInMemory = new Map<string, string>()

/** Whether this sign-in has had its tutorial in this browser. */
export function seenThisSignIn(slug: string, key: string): boolean {
    try {
        const stored = window.localStorage.getItem(storageKey(slug))
        if (stored !== null) return stored === key
    } catch {
        // Storage blocked: the page's memory below.
    }
    return seenInMemory.get(slug) === key
}

/** Remembers that this sign-in has had it (when it starts, so a reload mid-way doesn't start it over). */
export function markSeen(slug: string, key: string): void {
    seenInMemory.set(slug, key)
    try {
        window.localStorage.setItem(storageKey(slug), key)
    } catch {
        // Not remembered past this page.
    }
}
